import * as React from 'react';
import { RangeChartHelper, Bar } from 'common/rangeChartHelper';
import './rangePenetrationComparisonChart.scss';
import { isValidNumber, constrainToRange, determinePercentageIntoRange } from 'utils/numberUtils';

interface IRangePenetrationComparisonChartProps {
    className?: string;
    currentJobRangeBar: Nullable<Bar>;
    newJobRangeBar: Nullable<Bar>;
    pay: number;
    isNewRangeDisabled?: boolean;
}

class RangePenetrationComparisonChart extends React.Component<IRangePenetrationComparisonChartProps> {
    constructor(props: IRangePenetrationComparisonChartProps) {
        super(props);
    }

    render(): React.ReactNode {
        const { currentJobRangeBar, newJobRangeBar, pay, isNewRangeDisabled } = this.props;
        const className = this.props.className || '';
        // get the minimum and maximum range values based on both job ranges
        const minNum: number = RangeChartHelper.getMin(newJobRangeBar, currentJobRangeBar);
        const maxNum: Nullable<number> = RangeChartHelper.getMax(newJobRangeBar, currentJobRangeBar);
        const baseBarClassName = 'range-penetration-comparison-chart__bar';
        const newBarModifier = isNewRangeDisabled ? 'disabled' : 'new';
        const markerModifierClass = isNewRangeDisabled ? 'range-penetration-comparison-chart__marker--single-bar' : '';

        // provide actual numbers that make sense, please
        if (currentJobRangeBar == null ||
            newJobRangeBar == null ||
            maxNum == null ||
            !isValidNumber(pay) ||
            !isValidNumber(currentJobRangeBar.min) ||
            !isValidNumber(currentJobRangeBar.max) ||
            !isValidNumber(newJobRangeBar.min) ||
            !isValidNumber(newJobRangeBar.max) ||
            currentJobRangeBar.min < 0 ||
            currentJobRangeBar.max < 0 ||
            newJobRangeBar.min < 0 ||
            newJobRangeBar.max < 0 ||
            currentJobRangeBar.min >= currentJobRangeBar.max ||
            newJobRangeBar.min >= newJobRangeBar.max ||
            minNum >= maxNum
        ) {
            return null;
        }

        // positive numbers and zeroes only, please
        const payNum = pay <= 0 ? 0 : pay;
        // get percentages for styling
        const currentRangeLeftEdge = determinePercentageIntoRange(currentJobRangeBar.min, maxNum, minNum);
        const currentRangeRightEdge = determinePercentageIntoRange(currentJobRangeBar.max, maxNum, minNum);
        const currentRangeWidth = currentRangeRightEdge - currentRangeLeftEdge;
        const newRangeLeftEdge = determinePercentageIntoRange(newJobRangeBar.min, maxNum, minNum);
        const newRangeRightEdge = determinePercentageIntoRange(newJobRangeBar.max, maxNum, minNum);
        const newRangeWidth = newRangeRightEdge - newRangeLeftEdge;
        const percentageIntoRange = determinePercentageIntoRange(payNum, maxNum, minNum);
        // build styles, capping at 0 and 100% for display purposes
        const rangeFilledStyle = {
            left: `${constrainToRange(percentageIntoRange)}%`
        };
        const rangeCurrentStyle = {
            left: `${constrainToRange(currentRangeLeftEdge)}%`,
            right: `${constrainToRange(currentRangeRightEdge)}%`,
            width: `${constrainToRange(currentRangeWidth)}%`
        };
        const rangeNewStyle = {
            left: `${constrainToRange(newRangeLeftEdge)}%`,
            right: `${constrainToRange(newRangeRightEdge)}%`,
            width: `${constrainToRange(newRangeWidth)}%`
        };

        return (
            <div className={`range-penetration-comparison-chart ${className}`}>
                <div className={`${baseBarClassName} ${baseBarClassName}--current`} style={rangeCurrentStyle}>
                    <span className={`${baseBarClassName}-range ${baseBarClassName}-range--low`}>
                        {currentJobRangeBar.minText}
                    </span>
                    <span className={`${baseBarClassName}-range ${baseBarClassName}-range--high`}>
                        {currentJobRangeBar.maxText}
                    </span>
                    <div className={`${baseBarClassName}-chart`} />
                </div>
                <div className={`${baseBarClassName} ${baseBarClassName}--${newBarModifier}`} style={rangeNewStyle}>
                    <div className={`${baseBarClassName}-chart`} />
                    {!isNewRangeDisabled && (
                        <>
                            <span className={`${baseBarClassName}-range ${baseBarClassName}-range--low`}>
                                {newJobRangeBar.minText}
                            </span>
                            <span className={`${baseBarClassName}-range ${baseBarClassName}-range--high`}>
                                {newJobRangeBar.maxText}
                            </span>
                        </>
                    )}
                </div>
                <div
                    className={`range-penetration-comparison-chart__marker ${markerModifierClass}`}
                    style={rangeFilledStyle}
                />
            </div>
        );
    }
}

export default RangePenetrationComparisonChart;